/**
 * Stylitics "Style It With" Widget for Faherty
 * Enhanced version with robust container handling and fallback creation
 */

class StyliticsWidget {
  constructor() {
    this.composer = null;
    this.productId = null;
    this.settings = window.styliticsSettings || {};
    this.isInitialized = false;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.hasQuickShopFallbackListener = false;
  }

  /**
   * Initialize the widget - Optimized for speed
   */
  async init() {
    try {
      // Fast product ID detection (synchronous)
      this.detectProductId();
      if (!this.productId) {
        console.warn("No product ID detected. Stylitics widget will not load.");
        // Widget will be hidden automatically by Stylitics via load.data callback
        return;
      }

      // Ensure container exists (synchronous)
      this.ensureContainer();

      // Wait for Stylitics Composer library (optimized polling)
      await this.waitForComposer();

      // Initialize widget
      await this.initializeWidget();
    } catch (error) {
      console.error("Error initializing Stylitics widget:", error);

      // Retry logic with shorter delay
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(
          `Retrying Stylitics initialization (attempt ${this.retryCount}/${this.maxRetries})`,
        );
        setTimeout(() => this.init(), 1000); // Reduced from 2000ms to 1000ms
      } else {
        // Max retries reached, remove loading state
        // Widget will be hidden automatically by Stylitics via load.data callback
        const container = document.getElementById(
          "stylitics-composer-container",
        );
        if (container) {
          container.classList.remove("is-loading");
          container.setAttribute("aria-busy", "false");
        }
      }
    }
  }

  /**
   * Ensure the container exists, create if missing - SYNCHRONOUS for speed
   */
  ensureContainer() {
    let container = document.getElementById("stylitics-composer-container");

    if (!container) {
      console.log("Container not found, creating fallback...");
      container = this.createFallbackContainer();
    }

    if (!container) {
      throw new Error("Unable to create or find Stylitics container");
    }

    // Add loading class for visual feedback
    container.classList.add("is-loading");

    return container;
  }

  /**
   * Create fallback container if template doesn't include one
   */
  createFallbackContainer() {
    // Try to find a good insertion point
    const insertionPoints = [
      // After buy buttons
      document.querySelector(".product-info__buy-buttons"),
      document.querySelector('[data-block-type="buy_buttons"]'),
      // After product info
      document.querySelector(".product-info"),
      // After main product content
      document.querySelector(".shopify-section--main-product .product"),
      // After any product section
      document.querySelector(".shopify-section--main-product"),
      // Fallback to main content
      document.querySelector("main"),
      document.querySelector("#main-content"),
      document.body,
    ];

    const insertAfter = insertionPoints.find((el) => el !== null);

    if (!insertAfter) {
      return null;
    }

    // Create container with proper styling
    const container = document.createElement("div");
    container.id = "stylitics-composer-container";
    container.className = "gutters margin-top--l stylitics-fallback-container";
    container.style.marginTop = "2rem";
    container.style.scrollMarginTop = "100px";

    // Insert after the found element
    insertAfter.parentNode.insertBefore(container, insertAfter.nextSibling);

    return container;
  }

  /**
   * Detect current product ID using multiple methods - SYNCHRONOUS for speed
   */
  detectProductId() {
    // Method 1: Shopify Analytics (fastest)
    if (window.ShopifyAnalytics?.meta?.product?.id) {
      this.productId = window.ShopifyAnalytics.meta.product.id.toString();
      return;
    }

    // Method 2: Global product object (fast)
    if (window.product?.id) {
      this.productId = window.product.id.toString();
      return;
    }

    // Method 3: Theme global (fast)
    if (window.theme?.product?.id) {
      this.productId = window.theme.product.id.toString();
      return;
    }

    // Method 4: Meta property (requires DOM query)
    const metaProductId = document.querySelector(
      'meta[property="product:retailer_item_id"]',
    );
    if (metaProductId) {
      this.productId = metaProductId.getAttribute("content");
      return;
    }

    // Method 5: Script tag data (slowest - only if needed)
    const productScripts = document.querySelectorAll(
      'script[type="application/ld+json"]',
    );
    for (const script of productScripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (data["@type"] === "Product" && data.productID) {
          this.productId = data.productID.toString();
          return;
        }
      } catch (e) {
        // Continue searching
      }
    }
  }

  /**
   * Wait for StyliticsComposer to be available - OPTIMIZED with faster polling
   */
  async waitForComposer(timeout = 8000) {
    const startTime = Date.now();

    while (!window.StyliticsComposer) {
      if (Date.now() - startTime > timeout) {
        throw new Error("StyliticsComposer not loaded within timeout");
      }
      // Faster polling - check every 25ms instead of 100ms
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
  }

  /**
   * Wait for element to exist - OPTIMIZED with faster polling
   */
  async waitForElement(selector, timeout = 3000) {
    const startTime = Date.now();

    while (!document.querySelector(selector)) {
      if (Date.now() - startTime > timeout) {
        throw new Error(`Element ${selector} not found within timeout`);
      }
      // Faster polling
      await new Promise((resolve) => setTimeout(resolve, 20));
    }

    return document.querySelector(selector);
  }

  /**
   * Initialize the Stylitics widget
   */
  async initializeWidget() {
    try {
      // Get customer data - prioritize reliable Liquid-generated data
      const customerData = window.styliticsCustomer || {};
      const customerId = customerData.id || window.__VPV?.customer?.id || null;
      const customerEmail =
        customerData.email || window.__VPV?.customer?.email || null;

      this.composer = await StyliticsComposer.initialize({
        client: "faherty",
        target: "stylitics-composer-container",
        config: {
          modules: [
            {
              type: "classic",
              params: {
                api: {
                  item_number: this.productId,
                  min: this.settings.minBundles || 3,
                  max: this.settings.maxBundles || 6,
                },
                text: {
                  itemLinkCTA: this.settings.buttonText || "Shop",
                  moduleHeading: this.settings.heading || "Style It With",
                  moduleHeadingLevel: "h4",
                  modalHeadingLevel: "h6",
                },
              },
              on: {
                "mount.bundles": (data) => this.handleBundlesMount(data),
                "mount.bundle": (data) => this.handleBundleMount(data),
                "load.data": (data) => this.handleLoadData(data),
              },
              override: {
                "click.item": (data) => this.handleItemClick(data),
                "click.hotspot": (data) => this.handleHotspotClick(data),
              },
            },
          ],
          sharedParams: {
            customer: customerId
              ? {
                  clientUserId: customerId,
                  ...(customerEmail && { email: customerEmail }),
                }
              : {},
            analytics: {
              placement: "pdp",
            },
          },
        },
      });

      // Start the composer to trigger API calls and bundle loading
      await this.composer.start();

      this.setupStyliticsSvgA11yObserver();

      this.isInitialized = true;
      this.setupVariantChangeListener();
      this.setupAddToCartTracking();
    } catch (error) {
      console.error("Error initializing Stylitics widget:", error);
      throw error;
    }
  }

  /**
   * Handle bundles mount event
   */
  handleBundlesMount(data) {
    // Remove loading state
    const container = document.getElementById("stylitics-composer-container");
    if (container) {
      container.classList.remove("is-loading");
      container.setAttribute("aria-busy", "false");
      this.markStyliticsDecorativeSvgs(container);
    }

    // Wait for content to render, then create jumplink
    // Note: Stylitics automatically handles widget visibility based on min bundles
    if (data.bundles && data.bundles.length > 0) {
      setTimeout(() => {
        if (container) {
          this.markStyliticsDecorativeSvgs(container);
        }
        this.createJumpLinkIfNeeded();
        this.setupQuickShopLinkFallback();
      }, 1000);
    }
  }

  /**
   * Handle individual bundle mount for custom badges
   */
  handleBundleMount(data) {
    if (!data.bundle || !data.element) return;

    // Add "Style the Model" badge for on-model images
    if (data.bundle["on-model-image"]) {
      const badgeContainer = data.element.querySelector(
        ".stylitics-bundle-badge",
      );
      if (
        badgeContainer &&
        !badgeContainer.querySelector(".stylitics-stm-badge")
      ) {
        const badge = document.createElement("img");
        badge.className = "stylitics-stm-badge";
        badge.src =
          "https://cdn.shopify.com/s/files/1/0359/8357/files/STM.png?v=1736387449";
        badge.alt = "This outfit corresponds with model's on product image";
        badgeContainer.appendChild(badge);
      }
    }
  }

  /**
   * Handle load.data event - proper way to control jumplink visibility
   * Documentation: https://docs.stylitics.com/hc/en-us/articles/24049134586907-Add-a-Title-and-Jumplink
   * Note: Stylitics automatically handles widget visibility based on min bundles
   */
  handleLoadData(data) {
    const jumpLink = document.getElementById("stylitics-jumplink");

    if (!data.hidden && jumpLink) {
      // Widget is visible, show jumplink
      jumpLink.style.visibility = "visible";
      jumpLink.style.display = "block";
    } else if (data.hidden && jumpLink) {
      // Widget is hidden, hide jumplink
      jumpLink.style.visibility = "hidden";
      jumpLink.style.display = "none";
    }
  }

  /**
   * Handle item click - proper way to intercept product clicks
   * Documentation: https://docs.stylitics.com/hc/en-us/articles/24049676465691-Quickview-Integration-Item-Click
   * Note: Stylitics automatically fires the item.click tracking event, no manual tracking needed
   */
  handleItemClick(data) {
    this.openQuickShopFromStyliticsData(data);
  }

  /**
   * Handle hotspot click - proper way to intercept hotspot clicks on outfit images
   * Note: Stylitics automatically fires the hotspot.click tracking event, no manual tracking needed
   */
  handleHotspotClick(data) {
    this.openQuickShopFromStyliticsData(data);
  }

  /**
   * Resolve link data from Stylitics click payloads and open quick shop
   */
  openQuickShopFromStyliticsData(data) {
    const candidateLinks = [
      data?.item?.affiliate_link,
      data?.item?.url,
      data?.affiliate_link,
      data?.url,
      data?.element?.getAttribute?.("href"),
      data?.event?.target?.closest?.("a[href]")?.getAttribute?.("href"),
    ].filter(Boolean);

    const firstValidLink = candidateLinks.find((link) =>
      this.parseProductLink(link),
    );

    if (!firstValidLink) {
      console.warn("Stylitics: Could not resolve product link from click data");
      return;
    }

    this.openQuickShopFromLink(firstValidLink);
  }

  /**
   * Parse product handle/variant from a URL string
   */
  parseProductLink(link) {
    try {
      const parsedUrl = new URL(link, window.location.origin);
      const match = parsedUrl.pathname.match(/\/products\/([^/?#]+)/);
      if (!match || !match[1]) return null;

      return {
        handle: decodeURIComponent(match[1]),
        variantId: parsedUrl.searchParams.get("variant") || null,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Dispatch quick shop events used by both modal implementations
   */
  dispatchQuickShopEvents({ handle, variantId }) {
    const detail = {
      handle,
      variantId: variantId || null,
    };

    window.dispatchEvent(
      new CustomEvent("mini-modal:open", {
        detail,
      }),
    );

    window.dispatchEvent(
      new CustomEvent("quick-add:open", {
        detail,
      }),
    );

    this._lastStyliticsInteraction = Date.now();
  }

  /**
   * Open quick shop from a product link
   */
  openQuickShopFromLink(link) {
    const parsedLink = this.parseProductLink(link);
    if (!parsedLink) return;
    this.dispatchQuickShopEvents(parsedLink);
  }

  /**
   * Fallback delegated click handling for Stylitics product links
   */
  setupQuickShopLinkFallback() {
    if (this.hasQuickShopFallbackListener) return;

    const container = document.getElementById("stylitics-composer-container");
    if (!container) return;

    container.addEventListener("click", (event) => {
      const link = event.target.closest?.("a[href*='/products/']");
      if (!link) return;

      // Respect modified clicks (open new tab/window behavior)
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      this.openQuickShopFromLink(link.getAttribute("href"));
    });

    this.hasQuickShopFallbackListener = true;
  }

  /**
   * Create jump link badge in the top right of product gallery
   * This is called after Stylitics content is loaded to ensure proper positioning
   */
  createJumpLinkIfNeeded() {
    if (!this.settings.showJumpLink) return;

    let jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      jumpLink.style.visibility = "visible";
      jumpLink.style.display = "block";
      return;
    }

    // Look for product gallery/media containers to position the badge
    const galleryContainers = [
      // Try to find the main product media container
      document.querySelector(".product__media"),
      document.querySelector(".product-media"),
      document.querySelector("[data-media-gallery]"),
      document.querySelector(".product-gallery"),
      // Fallback to any media container
      document.querySelector(".media"),
      document.querySelector("[class*='media']"),
      // Last resort - main product section
      document.querySelector(".shopify-section--main-product"),
    ];

    const galleryContainer = galleryContainers.find((el) => el !== null);
    if (!galleryContainer) return;

    // Create jump link badge positioned in top right of gallery
    jumpLink = document.createElement("a");
    jumpLink.id = "stylitics-jumplink";
    jumpLink.href = "#stylitics-composer-container";
    jumpLink.className = "stylitics-jump-badge";
    jumpLink.style.cssText = `
      position: absolute;
      z-index: 10;
      display: inline-flex;
      align-items: center;
      padding: 5px 10px;
      background: #fcfaf6;
      color: #2b2e2f;
      text-decoration: none;
      font-size: 0.65rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      visibility: visible;
      box-shadow:0 0 0 1px rgb(var(--text-color) / .12);
    `;

    jumpLink.innerHTML = `
      ${this.settings.heading || "Style It With"} 
      <svg style="margin-left: 6px; width: 10px; height: 6px;" viewBox="0 0 12 8" fill="currentColor">
        <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    // Add hover effects
    jumpLink.addEventListener("mouseenter", () => {
      jumpLink.style.background = "#2b2e2f";
      jumpLink.style.color = "#FFFFFF";
      jumpLink.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
    });

    jumpLink.addEventListener("mouseleave", () => {
      jumpLink.style.background = "#FFFFFF";
      jumpLink.style.color = "#2b2e2f";
      jumpLink.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
    });

    // Add smooth scroll behavior with proper offset
    jumpLink.addEventListener("click", (e) => {
      e.preventDefault();
      const container = document.getElementById("stylitics-composer-container");
      if (!container) return;

      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        const headerOffset = 100;
        const elementPosition = container.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      });
    });

    // Ensure the gallery container has relative positioning
    const galleryPosition = window.getComputedStyle(galleryContainer).position;
    if (galleryPosition === "static") {
      galleryContainer.style.position = "relative";
    }

    // Insert the badge into the gallery container
    galleryContainer.appendChild(jumpLink);
  }

  /**
   * Refresh widget for new product
   */
  async refresh(newProductId) {
    if (!this.isInitialized || !this.composer) {
      return this.init();
    }

    if (this.productId === newProductId) {
      return;
    }

    this.productId = newProductId;

    try {
      await this.composer.refresh({
        modules: [
          {
            type: "classic",
            params: {
              api: {
                item_number: newProductId,
                min: this.settings.minBundles || 3,
                max: this.settings.maxBundles || 6,
              },
            },
          },
        ],
      });
    } catch (error) {
      console.error("Stylitics: Refresh failed:", error);
    }
  }

  /**
   * Setup listener for product changes when color swatches are clicked
   */
  setupVariantChangeListener() {
    // Primary: read the target product ID directly from the clicked swatch's
    // data attributes. This avoids the race condition of waiting for the DOM
    // to be replaced by the product-grouping fetch.
    document.addEventListener("click", (event) => {
      // Guard against event.target not being a DOM element
      if (!event.target || typeof event.target.closest !== "function") {
        return;
      }
      const swatchElement =
        event.target.closest(".product-swatch") ||
        event.target.closest('[class*="swatch"]') ||
        event.target.closest(".variant-picker button") ||
        event.target.closest(".product-grouping button");

      if (swatchElement) {
        const newProductId = swatchElement.dataset.productId;
        if (newProductId && newProductId !== this.productId) {
          this.refresh(newProductId);
        }
      }
    });

    // Fallback: listen for the productGroupingInjected event which fires
    // after the DOM has been fully replaced with the new product's HTML.
    // This catches any cases where the swatch element didn't carry a
    // data-product-id or the click selector didn't match.
    window.addEventListener("productGroupingInjected", () => {
      const newProductId = this.getUpdatedProductId();
      if (newProductId && newProductId !== this.productId) {
        this.refresh(newProductId);
      }
    });
  }

  /**
   * Setup tracking for add-to-cart events from Stylitics interactions
   */
  setupAddToCartTracking() {
    // Listen for variant:add events and track if they came from Stylitics
    document.addEventListener("variant:add", (event) => {
      // Check if this add-to-cart came from a recent Stylitics interaction
      const timeSinceInteraction = Date.now() - (this._lastStyliticsInteraction || 0);
      
      // 30-second window to associate ATC with Stylitics interaction
      if (timeSinceInteraction > 30000) {
        return;
      }

      const detail = event.detail;
      const items = detail.items || [detail];

      items.forEach((item) => {
        const itemId = item.product_id;
        if (itemId && this.composer?.modules?.[0]?.trackAddItemToCart) {
          this.composer.modules[0].trackAddItemToCart(itemId);
        }
      });

      // Clear the interaction timestamp after tracking
      this._lastStyliticsInteraction = null;
    });
  }

  /**
   * Get the updated product ID from the DOM after color swatch changes
   */
  getUpdatedProductId() {
    const mainProductInfo = document.querySelector("#main-product-info");
    if (mainProductInfo) {
      const productId = mainProductInfo.getAttribute("data-product-id");
      if (productId) {
        return productId;
      }
    }

    if (window.ShopifyAnalytics?.meta?.product?.id) {
      return window.ShopifyAnalytics.meta.product.id.toString();
    }

    return null;
  }

  /**
   * Mark decorative Stylitics arrow/control SVGs so they are not exposed as unnamed graphics.
   */
  markStyliticsDecorativeSvgs(root) {
    if (!root || typeof root.querySelectorAll !== "function") {
      return;
    }

    const selectors = [
      ".stylitics-ui-arrow-button svg",
      ".stylitics-arrow svg",
      ".stylitics-arrow-button svg",
      "button.stylitics-ui-arrow-button svg",
    ];

    for (const selector of selectors) {
      root.querySelectorAll(selector).forEach((svg) => {
        if (!svg || svg.tagName.toLowerCase() !== "svg") {
          return;
        }
        if (svg.getAttribute("aria-label")) {
          return;
        }
        const titleEl = svg.querySelector("title");
        if (titleEl && titleEl.textContent && titleEl.textContent.trim()) {
          return;
        }
        svg.setAttribute("aria-hidden", "true");
        svg.setAttribute("focusable", "false");
      });
    }
  }

  /**
   * Observe Stylitics DOM updates so dynamically injected arrow SVGs stay hidden from the a11y tree.
   */
  setupStyliticsSvgA11yObserver() {
    const container = document.getElementById("stylitics-composer-container");
    if (!container || typeof MutationObserver === "undefined") {
      return;
    }

    this.markStyliticsDecorativeSvgs(container);

    if (this._styliticsSvgObserver) {
      return;
    }

    this._styliticsSvgObserver = new MutationObserver(() => {
      this.markStyliticsDecorativeSvgs(container);
    });
    this._styliticsSvgObserver.observe(container, {
      childList: true,
      subtree: true,
    });
  }
}

// Initialize widget as early as possible - OPTIMIZED
(function () {
  let styliticsWidget;

  function initWidget() {
    if (!styliticsWidget) {
      styliticsWidget = new StyliticsWidget();
      // Expose instance for manual debugging after creation
      window.styliticsWidget = styliticsWidget;
    }
    styliticsWidget.init();
  }

  // Start initialization immediately if DOM is ready or interactive
  if (document.readyState === "loading") {
    // DOM still loading, wait for DOMContentLoaded
    document.addEventListener("DOMContentLoaded", initWidget);
  } else {
    // DOM is already ready (interactive or complete), initialize immediately
    initWidget();
  }

  // Also initialize on theme section loads (for AJAX navigation)
  document.addEventListener("shopify:section:load", (e) => {
    if (e.detail.sectionId.includes("main-product")) {
      // Reduced delay for faster response
      setTimeout(initWidget, 50);
    }
  });

  // Expose class for manual debugging
  window.StyliticsWidget = StyliticsWidget;
})();
